import { useState, useEffect } from 'react';
import axios from 'axios';
import SERVER from '../../hooks/useServer';
import { FaEdit, FaTrash, FaLock, FaUser } from 'react-icons/fa';
import { MdEmail } from 'react-icons/md';
import '../../styles/pages/Admin/Dashboard.scss';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Admins = ({ admins, setAdmins }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);
    const [isEditing, setIsEditing] = useState(false);
    const [currentAdmin, setCurrentAdmin] = useState(null);
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('')
    // Form states
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        confirmPassword: ''
    });

    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentAdmins = admins.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(admins.length / itemsPerPage);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEdit = (admin) => {
        setIsEditing(true);
        setCurrentAdmin(admin);
        setFormData({
            username: admin.username,
            email: admin.email,
            password: '',
            confirmPassword: ''
        });
        toast.info('Mode édition activé');
    };

    const handleCancel = () => {
        setIsEditing(false);
        setCurrentAdmin(null);
        setShowPasswordForm(false);
        setFormData({
            username: '',
            email: '',
            password: '',
            confirmPassword: ''
        });
    };

    const handleUpdateAdmin = async () => {
        // Validation
        if (!formData.username.trim()) {
            toast.error('Le nom d\'utilisateur est requis');
            return;
        }
        if (!formData.email.trim()) {
            toast.error('L\'email est requis');
            return;
        }
        if (formData.password && formData.password !== formData.confirmPassword) {
            toast.error('Les mots de passe ne correspondent pas');
            setError("Les mots de passe ne correspondent pas");
            return;
        }

        try {
            setLoading(true);
            const response = await axios.put(
                `${SERVER}/auth/${currentAdmin.id}`,
                {
                    username: formData.username,
                    email: formData.email,
                    ...(formData.password && { password: formData.password }),
                },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                    },
                }
            );

            setAdmins(admins.map(admin => 
                admin.id === currentAdmin.id ? response.data.admin : admin
            ));
            toast.success('Administrateur mis à jour avec succès');
            setError(null);
            handleCancel();
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.message;
            setError(errorMsg);
            toast.error(`Erreur: ${errorMsg}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAdmin = async (id) => {
        if (window.confirm("Êtes-vous sûr de vouloir supprimer cet administrateur ?")) {
            try {
                await axios.delete(`${SERVER}/auth/${id}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                setAdmins(admins.filter(admin => admin.id !== id));
                toast.success('Administrateur supprimé avec succès');
                setError(null);
            } catch (err) {
                const errorMsg = err.response?.data?.error || err.message;
                setError(errorMsg);
                toast.error(`Erreur: ${errorMsg}`);
            }
        }
    };

    const handleChangePassword = (admin) => {
        setCurrentAdmin(admin);
        setShowPasswordForm(true);
        setFormData({
            username: admin.username,
            email: admin.email,
            password: '',
            confirmPassword: ''
        });
    };

    const handleUpdatePassword = async () => {
        // Validation
        if (!currentPassword.trim()) {
            toast.error('L\'ancien mot de passe est requis');
            return;
        }
        if (!formData.password.trim()) {
            toast.error('Le nouveau mot de passe est requis');
            return;
        }
        if (formData.password.length < 6) {
            toast.error('Le mot de passe doit contenir au moins 6 caractères');
            return;
        }
        if (formData.password !== formData.confirmPassword) {
            toast.error('Les mots de passe ne correspondent pas');
            setError("Les mots de passe ne correspondent pas");
            return;
        }

        try {
            setLoading(true);
            await axios.put(
                `${SERVER}/auth/${currentAdmin.id}/password`,
                { currentPassword : currentPassword, newPassword: formData.password },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                    },
                }
            );
            toast.success('Mot de passe mis à jour avec succès');
            setError(null);
            setShowPasswordForm(false);
            setCurrentPassword('');
            setFormData({
                username: '',
                email: '',
                password: '',
                confirmPassword: ''
            });
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.message;
            setError(errorMsg);
            toast.error(`Erreur: ${errorMsg}`);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="loading">Loading...</div>;

    return (
        <div className="admin-section">
            {error && (
                <div className="error-message">
                    {error}
                    <button onClick={() => setError(null)}>×</button>
                </div>
            )}

            <h2>Admin Management</h2>

            {/* Admin List */}
            <div className="admin-table-container">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Username</th>
                            <th>Email</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentAdmins.map(admin => (
                            <tr key={admin.id}>
                                <td>{admin.username}</td>
                                <td>{admin.email}</td>
                                <td className="admin-actions">
                                    <button 
                                        onClick={() => handleEdit(admin)}
                                    >
                                        <FaEdit /> Edit
                                    </button>
                                    <button
                                        onClick={() => handleChangePassword(admin)}
                                    >
                                        <FaLock /> Password
                                    </button>
                                    <button
                                        onClick={() => handleDeleteAdmin(admin.id)}
                                    >
                                        <FaTrash /> Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* Pagination */}
                {admins.length > itemsPerPage && (
                    <div className="pagination">
                        {Array.from({ length: totalPages }).map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentPage(index + 1)}
                                className={currentPage === index + 1 ? 'active' : ''}
                            >
                                {index + 1}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Edit Admin Form */}
            {isEditing && (
                <div className="admin-form">
                    <h3>Edit Admin</h3>
                    <div className="form-group">
                        <FaUser className="input-icon" />
                        <input
                            type="text"
                            name="username"
                            placeholder="Username"
                            value={formData.username}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="form-group">
                        <MdEmail className="input-icon" />
                        <input
                            type="email"
                            name="email"
                            placeholder="Email"
                            value={formData.email}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="form-actions">
                        <button className="update-button" onClick={handleUpdateAdmin}>
                            Update Admin
                        </button>
                        <button className="cancel-button" onClick={handleCancel}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Change Password Form */}
            {showPasswordForm && (
                <div className="admin-form">
                    <h3>Change Password for {currentAdmin?.username}</h3>
                    <div className="form-group">
                        <FaLock className="input-icon" />
                        <input
                            type="password"
                            name="password"
                            placeholder="Ancien Mot de passe"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <FaLock className="input-icon" />
                        <input
                            type="password"
                            name="password"
                            placeholder="Nouveau Password"
                            value={formData.password}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="form-group">
                        <FaLock className="input-icon" />
                        <input
                            type="password"
                            name="confirmPassword"
                            placeholder="Confirmez Nouveau Mot de passe"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="form-actions">
                        <button className="update-button" onClick={handleUpdatePassword}>
                            Update Password
                        </button>
                        <button className="cancel-button" onClick={() => setShowPasswordForm(false)}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Admins;